import * as componentTypes from "../../constants/componentTypes";

/**
 * @class OrderData
 */
export default class OrderData {
    /**
     * @param {Number}  id
     * @param {String}  identifier
     * @param {Number}  status
     */
    constructor(id, identifier, status) {
        this._id = id;
        this._identifier = identifier;
        this._status = status;
        this._databaseName = null;
        this._product = null;
        this._fandeck = null;
        this._formula = null;
        this._base = null;
        this._package = null;
        this._dispensedPackageCount = 0;
        this._packageCount = 0;
        this._priceList = [];
        this._created = null;
        this._customerName = null;
        this._canAcquire = false;
        this._clientName = null;
        this._note = null;
        this._moreInfoInternal = null;
        this._formulaMoreInfo = null;
        this._databaseAvailability = componentTypes.ORDER_DATABASE_AVAILABLE_UNKNOWN_STATE;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - OrderData");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get identifier() {
        return this._identifier;
    }

    /**
     * @param {String} identifier
     */
    set identifier(identifier) {
        this._identifier = identifier;
    }

    /**
     * @return {Number}
     */
    get status() {
        return this._status;
    }

    /**
     * @param {Number} status
     */
    set status(status) {
        this._status = status;
    }

    /**
     * @return {String}
     */
    get databaseName() {
        return this._databaseName;
    }

    /**
     * @param {String} databaseName
     */
    set databaseName(databaseName) {
        this._databaseName = databaseName;
    }

    /**
     * @return {Object}
     */
    get product() {
        return this._product;
    }

    /**
     * @param {Object} product
     */
    set product(product) {
        this._product = product;
    }

    /**
     * @return {Object}
     */
    get fandeck() {
        return this._fandeck;
    }

    /**
     * @param {Object} fandeck
     */
    set fandeck(fandeck) {
        this._fandeck = fandeck;
    }

    /**
     * @return {String}
     */
    get formula() {
        return this._formula;
    }

    /**
     * @param {String} formula
     */
    set formula(formula) {
        this._formula = formula;
    }

    /**
     * @return {Object}
     */
    get base() {
        return this._base;
    }

    /**
     * @param {Object} base
     */
    set base(base) {
        this._base = base;
    }

    /**
     * @return {Object}
     */
    get package() {
        return this._package;
    }

    /**
     * @param {Object} newPackage
     */
    set package(newPackage) {
        this._package = newPackage;
    }

    /**
     * @return {Number}
     */
    get dispensedPackageCount() {
        return this._dispensedPackageCount;
    }

    /**
     * @param {Number} dispensedPackageCount
     */
    set dispensedPackageCount(dispensedPackageCount) {
        this._dispensedPackageCount = dispensedPackageCount;
    }

    /**
     * @return {Number}
     */
    get packageCount() {
        return this._packageCount;
    }

    /**
     * @param {Number} packageCount
     */
    set packageCount(packageCount) {
        this._packageCount = packageCount;
    }

    /**
     * @return {Array}
     */
    get priceList() {
        return this._priceList;
    }

    /**
     * @param {Number} priceList
     */
    set priceList(priceList) {
        this._priceList = priceList;
    }

    /**
     * @return {Object}
     */
    get created() {
        return this._created;
    }

    /**
     * @param {Object} created
     */
    set created(created) {
        this._created = created;
    }

    /**
     * @return {String}
     */
    get customerName() {
        return this._customerName;
    }

    /**
     * @param {String} customerName
     */
    set customerName(customerName) {
        this._customerName = customerName;
    }

    /**
     * @return {Boolean}
     */
    get canAcquire() {
        return this._canAcquire;
    }

    /**
     * @param {Boolean} canAcquire
     */
    set canAcquire(canAcquire) {
        this._canAcquire = canAcquire;
    }

    /**
     * @return {String}
     */
    get clientName() {
        return this._clientName;
    }

    /**
     * @param {String} clientName
     */
    set clientName(clientName) {
        this._clientName = clientName;
    }

    /**
     * @return {String}
     */
    get note() {
        return this._note;
    }

    /**
     * @param {String} note
     */
    set note(note) {
        this._note = note;
    }

    /**
     * @return {String}
     */
    get moreInfoInternal() {
        return this._moreInfoInternal;
    }

    /**
     * @param {String} moreInfoInternal
     */
    set moreInfoInternal(moreInfoInternal) {
        this._moreInfoInternal = moreInfoInternal;
    }

    /**
     * @return {String}
     */
    get formulaMoreInfo() {
        return this._formulaMoreInfo;
    }

    /**
     * @param {String} formulaMoreInfo
     */
    set formulaMoreInfo(formulaMoreInfo) {
        this._formulaMoreInfo = formulaMoreInfo;
    }

    /**
     * @return {Number}
     */
    get databaseAvailability() {
        return this._databaseAvailability;
    }

    /**
     * @param {Number} databaseAvailability
     */
    set databaseAvailability(databaseAvailability) {
        this._databaseAvailability = databaseAvailability;
    }

    /**
     * Clones OrderData object.
     *
     * @return  {Object}
     */
    clone = () => {
        let order = new OrderData(this._id, this._identifier, this._status);

        order.databaseName = this._databaseName;
        order.product = this._product;
        order.fandeck = this._fandeck;
        order.formula = this._formula;
        order.base = this._base;
        order.package = this._package;
        order.dispensedPackageCount = this._dispensedPackageCount;
        order.packageCount = this._packageCount;
        order.priceList = this._priceList;
        order.created = this._created;
        order.customerName = this._customerName;
        order.canAcquire = this._canAcquire;
        order.clientName = this._clientName;
        order.note = this._note;
        order.moreInfoInternal = this._moreInfoInternal;
        order.formulaMoreInfo = this._formulaMoreInfo;
        order.databaseAvailability = this._databaseAvailability;

        return order;
    };

    /**
     * Clones OrderData object and updates formula property.
     *
     * @param   {Object} formula
     * @return  {Object}
     */
    updateFormula = (formula) => {
        let order = this.clone();
        order.formula = formula;

        return order;
    };

    /**
     * Clones OrderData object and updates package property.
     *
     * @param   {Number}    imageId
     * @return  {Object}
     */
    updateImageId = (imageId) => {
        let order = this.clone();
        order.package = this._package.updateImageId(imageId);

        return order;
    };

    /**
     * Clones OrderData object and updates databaseAvailability property.
     *
     * @param   {Number} databaseAvailability
     * @return  {Object}
     */
    updateDatabaseAvailability = (databaseAvailability) => {
        let order = this.clone();
        order.databaseAvailability = databaseAvailability;

        return order;
    };

    /**
     * Clones OrderData object and updates colorant dispense logic of formula property.
     *
     * @param   {String}    colorantName
     * @param   {Number}    dispenseLogic
     * @return  {Object}
     */
    updateColorantDispenseLogic = (colorantName, dispenseLogic) => {
        let order = this.clone();
        order.formula = this.formula.updateColorantDispenseLogic(colorantName, dispenseLogic);

        return order;
    };

    /**
     * Clones OrderData object and updates manual colorant list of formula property.
     *
     * @param   {Array}     colorantList
     * @return  {Object}
     */
    updateManualColorantList = (colorantList) => {
        let order = this.clone();
        order.formula = this.formula.updateColorantListMachine(colorantList);

        return order;
    };

    /**
     * Clones OrderData object and updates note property.
     *
     * @param   {String | null} note
     * @return  {Object}
     */
    updateNote = (note) => {
        let order = this.clone();
        order.note = note;

        return order;
    };

    /**
     * @return {String}
     */
    toString() {
        return "OrderData (" + this._id + ")";
    }
}
